import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  CircularProgress,
  Typography,
  Alert,
  Button,
} from '@mui/material';
import { Close } from '@mui/icons-material';
// @ts-ignore
import WebViewer from '@pdftron/webviewer';

const WEBVIEWER_LICENSE =
  'demo:1770900361581:6082a73403000000008bfdc578db599d0904b024b2339e144c1e745ec5';

export interface PdfViewerDialogProps {
  open: boolean;
  onClose: () => void;
  pdfUrl: string | null;
  title?: string;
}

export const PdfViewerDialog: React.FC<PdfViewerDialogProps> = ({
  open,
  onClose,
  pdfUrl,
  title,
}) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<any>(null);
  const [isViewerReady, setIsViewerReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !pdfUrl) {
      return undefined;
    }

    const dispose = () => {
      if (instanceRef.current) {
        try {
          instanceRef.current.UI.dispose();
        } catch {
          /* ignore */
        }
        instanceRef.current = null;
      }
      setIsViewerReady(false);
    };

    const timeout = setTimeout(() => {
      dispose();
      setError(null);

      if (!viewerRef.current) {
        return;
      }

      WebViewer(
        {
          path: '/webviewer',
          licenseKey: WEBVIEWER_LICENSE,
          initialDoc: pdfUrl,
          fullAPI: true,
          disableStreaming: true,
        } as any,
        viewerRef.current
      )
        .then((inst) => {
          instanceRef.current = inst;
          inst.Core.annotationManager.enableReadOnlyMode();
          inst.Core.documentViewer.addEventListener('documentLoaded', () => {
            inst.UI.setZoomLevel(1);
          });
          inst.Core.documentViewer.addEventListener('documentLoadError', () => {
            setError('Failed to load PDF. Try opening it in a new tab.');
          });
          setIsViewerReady(true);
        })
        .catch(() => {
          setError('Failed to initialize PDF viewer.');
        });
    }, 100);

    return () => {
      clearTimeout(timeout);
      dispose();
    };
  }, [open, pdfUrl]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="pdf-viewer-title"
      PaperProps={{ sx: { minHeight: '85vh' } }}
    >
      <DialogTitle
        id="pdf-viewer-title"
        sx={{
          fontFamily: 'Gotham',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        {title}
        <IconButton onClick={onClose} aria-label="Close document viewer" size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, height: '75vh', position: 'relative' }}>
        {error && (
          <Box sx={{ p: 2 }}>
            <Alert
              severity="error"
              action={
                pdfUrl ? (
                  <Button color="inherit" size="small" href={pdfUrl} target="_blank" rel="noopener noreferrer">
                    Open in new tab
                  </Button>
                ) : undefined
              }
            >
              {error}
            </Alert>
          </Box>
        )}
        {open && !isViewerReady && !error && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 1,
            }}
          >
            <CircularProgress sx={{ color: '#446388' }} />
            <Typography variant="body2" sx={{ mt: 2, fontFamily: 'Gotham' }}>
              Loading document...
            </Typography>
          </Box>
        )}
        <div ref={viewerRef} style={{ height: '100%', width: '100%' }} />
      </DialogContent>
    </Dialog>
  );
};
